/* The ancestor-ascent behind `isVisible`. `evaluate` walks the shadow-piercing ancestor
 * chain to <body>; `classify` applies a single ancestor's hide rules in CSS-cascade order.
 * The instance holds the geometric state the checks ground on, and the visited `chain`
 * (= the exact nodes a verdict covers, hence the cache write coverage). Rects stay lazy.
 * "box" = a box-generating ancestor (renders its CSS principal box; `display: contents`
 * generates none). Cross-mode cache subtleties live in cache.ts; this class only reads the
 * cache for short-circuits and exposes `coverage` / `hiddenByOpacity` for the write. */

import type { MaybeNull } from "@protontech/autofill/types/utils";
import { shadowPiercingAncestors } from "@protontech/autofill/utils/shadow-dom";
import { visibilityCache } from "./cache";
import type { LazyRect } from "./geometry";
import { containedInAncestor, isClipped, isNegligibleRect, lazyRect, scrollerReachable } from "./geometry";
import type { Viewport } from "./viewport";

export type VisibilityWalkInit = {
  /** Element whose visibility we're deciding. */
  element: HTMLElement;
  /** Its owning window (for `getComputedStyle`). */
  win: Window;
  /** Its owning document (for the <body> sentinel). */
  doc: Document;
  /** Viewport model for this document. */
  viewport: Viewport;
  /** Whether `opacity: 0` counts as hidden (the two feature modes differ here). */
  opacity: boolean;
  /** Whether to consult and short-circuit on the verdict cache. */
  useCache: boolean;
};

export class VisibilityWalk {
  /* Visited ancestors = the nodes the verdict covers (the cache write coverage). */
  private readonly chain: HTMLElement[] = [];
  /* Hidden purely by an `opacity: 0` ancestor (kept out of the opacity-independent table). */
  private transparent = false;

  /* Grounding state, accumulated as we ascend; each rect grounds a DIFFERENT check:
   *   scroller  while set it mediates: a scroller scrolls our content into its own box,
   *             so the anchor checks test THAT box, not the element's own.
   *   innerBox  nearest box-generating ancestor, captured once. Only a fallback rect for a
   *             boxless element (negligible rect + children, e.g. `display: contents`).
   *   prevBox   box ancestor we last came up from: its rect grounds the `fixed` check,
   *             its `absolute` flag the static-ancestor skip. */
  private scroller: MaybeNull<{ rect: LazyRect; index: number }> = null;
  private innerBox: MaybeNull<DOMRect> = null;
  private prevBox: MaybeNull<{ rect: LazyRect; absolute: boolean }> = null;

  private readonly element: HTMLElement;
  private readonly win: Window;
  private readonly doc: Document;
  private readonly viewport: Viewport;
  private readonly opacity: boolean;
  private readonly useCache: boolean;

  /* The element's own bounding rect, memoised once: what `anchor` grounds on. */
  private readonly ownRect: LazyRect;

  constructor({ element, win, doc, viewport, opacity, useCache }: VisibilityWalkInit) {
    this.element = element;
    this.win = win;
    this.doc = doc;
    this.viewport = viewport;
    this.opacity = opacity;
    this.useCache = useCache;
    this.ownRect = lazyRect(element);
  }

  /** The rect upward checks test (clip containment, scroller reach, body reachability),
   * by priority. Reflects the grounding crossed so far. `null` = a boxless chain with no
   * box yet (no evidence). */
  private anchor(): MaybeNull<DOMRect> {
    /* A crossed scroller mediates: it can scroll our content into its own box. */
    const scrollerBox = this.scroller?.rect();
    if (scrollerBox) return scrollerBox;
    /* Boxless element (no own box but has children, eg: display:contents): ground on the
     * nearest box ancestor instead. `childElementCount` is a free native read. */
    if (this.element.childElementCount > 0 && isNegligibleRect(this.ownRect())) return this.innerBox;
    /* Plain element: its own box grounds the checks. */
    return this.ownRect();
  }

  /** Does the anchor satisfy `check`? A `null` anchor passes. On a miss, trim the chain to
   * this hidden verdict's grounding node (nodes above aren't covered), then fail. The single
   * shape behind root reachability, clip containment and scroller reach. */
  private satisfies(check: (anchor: DOMRect) => boolean): boolean {
    const anchor = this.anchor();
    if (!anchor || check(anchor)) return true;
    this.chain.length = this.scroller ? this.scroller.index + 1 : Math.min(this.chain.length, 1);
    return false;
  }

  private visit(ancestor: HTMLElement): void {
    this.chain.push(ancestor);
  }

  /** Drop the last visited ancestor (one the verdict doesn't cover). */
  private unvisit(): void {
    this.chain.pop();
  }

  /** Record the crossed scroller; its rect mediates later anchor checks. */
  private registerScroller(rect: LazyRect): void {
    this.scroller = { rect, index: this.chain.length - 1 };
  }

  /** Record a box-generating ancestor. Both rects stay lazy: `prevBox.rect` is computed only
   * if a later `fixed` check reads it; `innerBox` only for a boxless subject. */
  private registerBox(box: LazyRect, absolute: boolean): void {
    /* Only a subject with children can be boxless (the negligible-rect half is tested in
     * `anchor`), so skip the rect work otherwise; keep the nearest non-negligible box. */
    if (this.innerBox === null && this.element.childElementCount > 0) {
      const rect = box();
      if (!isNegligibleRect(rect)) this.innerBox = rect;
    }
    this.prevBox = { rect: box, absolute };
  }

  /** `display: contents` generates no box, so it leaves no rect reference. */
  private clearBox(): void {
    this.prevBox = null;
  }

  /** A cached ancestor verdict, when it soundly answers THIS walk. A hidden ancestor hides
   * every walk through it. A visible one only certifies the chain ABOVE it, so trust it
   * only when the anchor is root-reachable; otherwise a scroller above could still rescue
   * it, so return `null` to fall through to the full ascent. `null` = no usable verdict. */
  private trustCache(ancestor: HTMLElement): MaybeNull<boolean> {
    if (!this.useCache) return null;
    const cached = visibilityCache.read(ancestor, this.opacity);
    if (cached === undefined) return null;
    if (!cached) return false;

    const anchor = this.anchor();
    return !anchor || this.viewport.canScrollIntoView(anchor) ? true : null;
  }

  /** Apply one ancestor's hide rules. Returns a terminal boolean verdict, or `null` to keep
   * ascending (state already updated). The ORDER is load bearing: DOM-hierarchy hides
   * (opacity/visibility/display, which hide absolute descendants too) precede the
   * static-ancestor skip; positioning-context hides (clip/overflow) follow it. */
  private classify(ancestor: HTMLElement, style: CSSStyleDeclaration, getRect: LazyRect): MaybeNull<boolean> {
    this.visit(ancestor);

    /* `opacity < 0.1` = hidden, only when opacity matters. */
    if (Math.floor(parseFloat(style.opacity) * 10) === 0 && this.opacity) {
      this.transparent = true;
      return false;
    }

    if (style.visibility === "hidden" || style.display === "none") return false;

    /* An absolute's positioning context is its nearest non-static ancestor: skip static
     * ones (they don't clip/scroll it). Hence clip/overflow are checked after this skip. */
    if (this.prevBox?.absolute && style.position === "static") {
      this.unvisit();
      return null;
    }

    if (isClipped(style.clip, style.clipPath)) return false;

    const { overflowX, overflowY } = style;
    const clippedX = overflowX === "hidden" || overflowX === "clip";
    const clippedY = overflowY === "hidden" || overflowY === "clip";

    if (clippedX || clippedY) {
      /* Anchor must overlap the clip box on the clipped axes. (Interleaved scrollers/
       * clippers degrade to inner-scroller-vs-outer-clipper; rare enough not to track.) */
      if (!this.satisfies((a) => containedInAncestor(a, getRect(), { x: clippedX, y: clippedY }))) return false;
      /* A negligible-sized clipper hides its content outright. */
      if (clippedX && clippedY && isNegligibleRect(getRect())) return false;
    }

    /* A scroller counts only if the axis truly overflows: CSS coerces `overflow: visible`
     * to `auto` when the other axis isn't visible (so `overflow-x: hidden` computes
     * `overflowY: auto`), and such clippers must not mask the field's body reachability. */
    const scrollableX = (overflowX === "auto" || overflowX === "scroll") && ancestor.scrollWidth > ancestor.clientWidth;
    const scrollableY = (overflowY === "auto" || overflowY === "scroll") && ancestor.scrollHeight > ancestor.clientHeight;

    if (scrollableX || scrollableY) {
      const rect = getRect();
      /* A negligible-size scroller hides everything it scrolls. */
      if (isNegligibleRect(rect)) return false;
      /* Scroller must reach the anchor; off-screen absolutes are caught here too (they
       * drag descendant rects along). */
      if (!this.satisfies((a) => scrollerReachable(a, rect, ancestor))) return false;
      this.registerScroller(getRect);
    }

    if (style.position === "fixed") {
      /* Fixed never scrolls: on screen now or not at all, grounded on the last box child
       * (a hidden verdict doesn't cover the fixed node, hence `unvisit`). */
      const inView = this.viewport.isInView(this.prevBox ? this.prevBox.rect() : getRect());
      if (!inView && this.prevBox) this.unvisit();
      return inView;
    }

    /* `display: contents` generates no box: drop the box reference and keep ascending. */
    if (style.display === "contents") {
      this.clearBox();
      return null;
    }

    this.registerBox(getRect, style.position === "absolute");
    return null;
  }

  /** Walk the shadow-piercing ancestor chain to <body>, hiding on any ancestor's style or
   * geometry, else visible if the root scroller can bring the anchor on screen. */
  evaluate(): boolean {
    for (const ancestor of shadowPiercingAncestors(this.element) as Generator<HTMLElement>) {
      /* At <body>, visible iff the root scroller can bring the anchor on screen (catches
       * e.g. a static field at `margin-left: 200vw`). */
      if (ancestor === this.doc.body) return this.satisfies((rect) => this.viewport.canScrollIntoView(rect));

      const cached = this.trustCache(ancestor);
      if (cached !== null) return cached;

      const getRect = ancestor === this.element ? this.ownRect : lazyRect(ancestor);
      const verdict = this.classify(ancestor, this.win.getComputedStyle(ancestor), getRect);
      if (verdict !== null) return verdict;
    }

    return true;
  }

  /** The nodes this walk's verdict covers (already trimmed):
   * the cache write coverage. */
  get coverage(): HTMLElement[] {
    return this.chain;
  }

  /** Whether the verdict is an opacity-only hide
   * (kept out of the opacity-independent table). */
  get hiddenByOpacity(): boolean {
    return this.transparent;
  }
}
